import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Users, UserPlus, UserMinus, Mail, Check, X, Loader2, Sparkles } from 'lucide-react';

export default function FriendsPage() {
  const { authHeaders, API } = useAuth();
  const [friends, setFriends] = useState([]);
  const [requests, setRequests] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [friendEmail, setFriendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState('friends');

  const fetchAll = async () => {
    try {
      const [f, r, s] = await Promise.all([
        axios.get(`${API}/friends`, authHeaders()),
        axios.get(`${API}/friends/requests`, authHeaders()),
        axios.get(`${API}/friends/suggestions`, authHeaders()),
      ]);
      setFriends(f.data);
      setRequests(r.data);
      setSuggestions(s.data);
    } catch (err) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line

  const sendRequest = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API}/friends/request`, { to_email: friendEmail }, authHeaders());
      toast.success(res.data.message);
      setFriendEmail('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally { setSending(false); }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/accept/${requestId}`, {}, authHeaders());
      toast.success('Friend request accepted!');
      fetchAll();
    } catch (err) { toast.error('Failed to accept'); }
  };

  const declineRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/decline/${requestId}`, {}, authHeaders());
      toast.success('Request declined');
      fetchAll();
    } catch (err) { toast.error('Failed to decline'); }
  };

  const removeFriend = async (friendId) => {
    if (!window.confirm('Remove this friend?')) return;
    try {
      await axios.delete(`${API}/friends/${friendId}`, authHeaders());
      toast.success('Friend removed');
      fetchAll();
    } catch (err) { toast.error('Failed to remove'); }
  };

  const sendRequestToSuggestion = async (email) => {
    try {
      const res = await axios.post(`${API}/friends/request`, { to_email: email }, authHeaders());
      toast.success(res.data.message);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    }
  };

  return (
    <div className="min-h-screen bg-[#F2F7F4] texture-grain">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <div className="animate-fade-in mb-6">
          <h1 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[#1B4332]" data-testid="friends-title">Friends</h1>
          <p className="text-[#4B6B58] mt-1">Connect with other equestrians on Stable Solutions.</p>
        </div>

        {/* Add Friend */}
        <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(27,67,50,0.06)] mb-6 animate-fade-in-delay-1">
          <h3 className="font-semibold text-[#1B4332] mb-3 flex items-center gap-2"><UserPlus size={16} /> Add a Friend</h3>
          <form onSubmit={sendRequest} className="flex gap-3">
            <div className="relative flex-1">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B6B58]" />
              <input
                type="email"
                value={friendEmail}
                onChange={e => setFriendEmail(e.target.value)}
                placeholder="Enter their email address"
                required
                data-testid="friend-email-input"
                className="stable-input w-full pl-10"
              />
            </div>
            <button type="submit" disabled={sending} data-testid="send-request-button" className="px-6 py-2.5 rounded-full bg-[#1B4332] text-white text-sm font-semibold btn-lift hover:bg-[#2D6A4F] disabled:opacity-50">
              {sending ? <Loader2 size={16} className="animate-spin" /> : 'Send'}
            </button>
          </form>
        </div>

        {/* Pending Requests */}
        {requests.length > 0 && (
          <div className="bg-[#FEF3C7] rounded-[2rem] p-6 shadow-[0_4px_12px_rgb(146,64,14,0.06)] mb-6 animate-fade-in-delay-1">
            <h3 className="font-semibold text-[#92400E] mb-3">Friend Requests ({requests.length})</h3>
            <div className="space-y-3">
              {requests.map(req => (
                <div key={req.request_id} className="flex items-center justify-between bg-white rounded-xl px-4 py-3" data-testid={`request-${req.request_id}`}>
                  <div>
                    <p className="text-sm font-medium text-[#1B4332]">{req.from_user?.name}</p>
                    <p className="text-xs text-[#4B6B58]">{req.from_user?.email}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => acceptRequest(req.request_id)} data-testid={`accept-${req.request_id}`} className="p-2 rounded-full bg-[#2D6A4F] text-white hover:bg-[#1B4332]"><Check size={14} /></button>
                    <button onClick={() => declineRequest(req.request_id)} data-testid={`decline-${req.request_id}`} className="p-2 rounded-full bg-red-100 text-red-500 hover:bg-red-200"><X size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-full p-1 flex gap-1 shadow-[0_2px_8px_rgb(27,67,50,0.04)] mb-6 w-fit">
          <button onClick={() => setTab('friends')} className={`px-5 py-2 rounded-full text-sm font-medium ${tab === 'friends' ? 'bg-[#1B4332] text-white' : 'text-[#4B6B58]'}`} data-testid="tab-friends">
            My Friends ({friends.length})
          </button>
          <button onClick={() => setTab('suggestions')} className={`px-5 py-2 rounded-full text-sm font-medium ${tab === 'suggestions' ? 'bg-[#1B4332] text-white' : 'text-[#4B6B58]'}`} data-testid="tab-suggestions">
            Suggestions ({suggestions.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 size={24} className="animate-spin text-[#2D6A4F] mx-auto" /></div>
        ) : tab === 'friends' ? (
          friends.length === 0 ? (
            <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]">
              <Users size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">No Friends Yet</h3>
              <p className="text-sm text-[#4B6B58]">Add friends by email or check out the suggestions tab.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {friends.map(f => (
                <div key={f.id} className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_12px_rgb(27,67,50,0.04)] flex items-center justify-between" data-testid={`friend-${f.id}`}>
                  <div>
                    <p className="font-medium text-[#1B4332]">{f.name}</p>
                    <p className="text-xs text-[#4B6B58]">{f.email}</p>
                  </div>
                  <button onClick={() => removeFriend(f.id)} className="p-2 rounded-full hover:bg-red-50 text-red-400" title="Remove friend">
                    <UserMinus size={14} />
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          suggestions.length === 0 ? (
            <div className="bg-white rounded-[2rem] p-12 text-center shadow-[0_8px_30px_rgb(27,67,50,0.06)]">
              <Sparkles size={32} className="text-[#4B6B58]/40 mx-auto mb-3" />
              <h3 className="font-['Cormorant_Garamond'] text-xl font-bold text-[#1B4332] mb-2">No Suggestions</h3>
              <p className="text-sm text-[#4B6B58]">As more people join, we'll suggest connections for you.</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4">
              {suggestions.map(s => (
                <div key={s.id} className="bg-white rounded-[1.5rem] p-5 shadow-[0_4px_12px_rgb(27,67,50,0.04)] flex items-center justify-between" data-testid={`suggestion-${s.id}`}>
                  <div>
                    <p className="font-medium text-[#1B4332]">{s.name}</p>
                    <p className="text-xs text-[#4B6B58]">{s.email}</p>
                  </div>
                  <button onClick={() => sendRequestToSuggestion(s.email)} data-testid={`add-suggestion-${s.id}`} className="p-2 rounded-full bg-[#E9EFE8] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white">
                    <UserPlus size={14} />
                  </button>
                </div>
              ))}
            </div>
          )
        )}
      </main>
    </div>
  );
}
